import React, { useState, useEffect } from 'react';
import { 
  Container, Dialog, DialogContent, DialogTitle, DialogActions, 
  TextField, Typography, Button, TableContainer, Table, TableHead, 
  TableBody, TableRow, TableCell, Paper, Box, Chip, Snackbar, Alert,
  IconButton, Card, CardContent, Grid, useMediaQuery, useTheme,
  Divider, Badge, Fab, InputAdornment
} from '@mui/material';
import { MenuInicial } from "./MenuInicial";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL, VENTASLISTAR, VENTADETALLEVER } from '../services/apiConfig';
import { DataGrid } from '@mui/x-data-grid';
import Cargando from './utils/Cargando.jsx';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './DatePickerStyles.css';
import * as XLSX from 'xlsx';
import { 
  SaveAlt, AddCircle, Visibility, Search, Close,
  Receipt, AttachMoney, CalendarToday, Person, Store
} from '@mui/icons-material';

// Función para formatear moneda
const formatCurrency = (value) => {
  return new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value || 0);
};

function VentasList() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Estados
  const [loading, setLoading] = useState(true);
  const [ventas, setVentas] = useState([]);
  const [selectedVenta, setSelectedVenta] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [ventaDetalles, setVentaDetalles] = useState([]);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredVentas, setFilteredVentas] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const userData = JSON.parse(sessionStorage.getItem("userData") || '{}');
  const sucursalid = userData?.sucursalid;

  useEffect(() => {
    const token = sessionStorage.getItem("authToken");
    if (!token) {
      navigate("/login");
      return;
    }

    const today = new Date();
    const formattedDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    fetchVentas(formattedDate, formattedDate);
  }, []);

  const columns = [
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 100,
      renderCell: (params) => (
        <IconButton onClick={() => handleVer(params.row)} color="primary">
          <Visibility />
        </IconButton>
      ),
    },
    { field: 'id', headerName: 'ID', width: 70 },
    { 
      field: 'fecha', 
      headerName: 'Fecha', 
      width: 155,
      valueFormatter: (params) => {  
        const fecha = new Date(params.value);
        return fecha.toLocaleDateString('es-ES', {
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit', hour12: false,
        });
      }
    },
    { field: 'sucursal', headerName: 'Sucursal', width: 120 },
    { field: 'vendedor', headerName: 'Vendedor', width: 130 },
    { field: 'cliente', headerName: 'Cliente', width: 150 },
    { 
      field: 'tipo_venta', 
      headerName: 'Tipo', 
      width: 100,
      renderCell: (params) => {
        const color = params.value === 'mesa' ? 'info' : 'default';
        const label = params.value === 'mesa' ? 'Mesa' : params.value === 'delivery' ? 'Delivery' : 'Mostrador';
        return <Chip label={label} color={color} size="small" />;
      }
    },
    { 
      field: 'total', 
      headerName: 'Total', 
      width: 100,
      valueFormatter: (params) => `$${formatCurrency(params.value)}`
    },
    { 
      field: 'descuento', 
      headerName: 'Descuento', 
      width: 100,
      valueFormatter: (params) => `$${formatCurrency(params.value)}`
    },
    { field: 'metodo_pago', headerName: 'Método de Pago', width: 130 },
    { 
      field: 'estado', 
      headerName: 'Estado', 
      width: 120,
      renderCell: (params) => {
        const color = params.value === 'completada' ? 'success' : 
                     params.value === 'pendiente' ? 'warning' : 'error';
        return <Chip label={params.value} color={color} size="small" />;
      }
    },
    { field: 'observaciones', headerName: 'Observaciones', width: 200 },
  ]; 

  const fetchVentas = async (startDateStr, endDateStr) => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem("authToken");
      console.log('Fetching ventas with params:', { s: sucursalid, startDate: startDateStr, endDate: endDateStr });
      const response = await axios.get(`${API_BASE_URL + VENTASLISTAR}`, {
        params: { s: sucursalid, startDate: startDateStr, endDate: endDateStr },
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('Ventas response:', response.data);
      
      const ventasConId = Array.isArray(response.data) 
        ? response.data.map((venta, index) => ({ ...venta, id: venta.id || index }))
        : [];
      
      setVentas(ventasConId);
      setFilteredVentas(ventasConId);
    } catch (error) {
      console.error('Error fetching ventas:', error);
      handleApiError(error, 'Error al cargar ventas');
    } finally {
      setLoading(false);
    }
  };
  
  const handleVer = async (venta) => {
    try {
      const token = sessionStorage.getItem("authToken");
      const response = await axios.get(`${API_BASE_URL + VENTADETALLEVER}`, {
        params: { id: venta.id },
        headers: { 'Authorization': `Bearer ${token}` } 
      });
      
      if (response.data) {
        if (response.data.venta && response.data.detalles) {
          setSelectedVenta(response.data.venta);
          setVentaDetalles(response.data.detalles);
        } else if (Array.isArray(response.data)) {
          setSelectedVenta(venta);
          setVentaDetalles(response.data);
        } else {
          setSelectedVenta(response.data);
          setVentaDetalles([]);
        }
        setIsDialogOpen(true);
      }
    } catch (error) {
      handleApiError(error, 'Error al cargar detalles');
    }
  };

  const handleApiError = (error, defaultMsg) => {
    console.error(defaultMsg, error);
    if (error.response?.status === 401) {
      sessionStorage.clear();
      navigate("/");
    } else {
      setSnackbar({ 
        open: true, 
        message: defaultMsg + ': ' + (error.response?.data?.message || error.message), 
        severity: 'error' 
      });
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedVenta(null);
    setVentaDetalles([]);
  };

  const handleSearch = () => {
    const startDateStr = startDate.toISOString().split('T')[0];
    const endDateStr = endDate.toISOString().split('T')[0];
    fetchVentas(startDateStr, endDateStr);
  };

  const handleSearchTermChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    const filtered = ventas.filter((venta) =>
      venta.cliente?.toLowerCase().includes(value.toLowerCase()) ||
      venta.id?.toString().includes(value) ||
      venta.observaciones?.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredVentas(filtered);
  };

  const exportToExcel = () => {
    if (filteredVentas.length === 0) {
      setSnackbar({ open: true, message: 'No hay datos para exportar', severity: 'warning' });
      return;
    }
    const dataToExport = filteredVentas.map(venta => ({
      ID: venta.id,
      Fecha: new Date(venta.fecha).toLocaleString('es-ES'),
      Sucursal: venta.sucursal,
      Vendedor: venta.vendedor,
      Cliente: venta.cliente,
      Total: venta.total,
      Descuento: venta.descuento,
      'Método de Pago': venta.metodo_pago,
      Estado: venta.estado,
      Observaciones: venta.observaciones
    }));
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Ventas');
    XLSX.writeFile(workbook, `ventas_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const calcularSubtotal = () => {
    return ventaDetalles.reduce((sum, item) => sum + parseFloat(item.subtotal || 0), 0);
  };

  // ==========================================
  // VISTA MÓVIL CON CARDS
  // ==========================================
  const MobileView = () => (
    <Box sx={{ pb: 10 }}>
      {/* Header sticky */}
      <Paper elevation={2} sx={{ p: 2, mb: 2, position: 'sticky', top: 56, zIndex: 100 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            <Receipt sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
            Ventas
          </Typography>
          <Chip label={`${filteredVentas.length} ventas`} color="primary" size="small" />
        </Box>

        <TextField
          placeholder="Buscar por cliente o ID..."
          variant="outlined"
          fullWidth
          size="small"
          value={searchTerm}
          onChange={handleSearchTermChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1.5 }}
        />

        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <DatePicker
            selected={startDate}
            onChange={(date) => setStartDate(date)}
            dateFormat="dd/MM/yyyy"
            customInput={
              <TextField 
                size="small" 
                fullWidth 
                InputProps={{
                  startAdornment: <CalendarToday sx={{ mr: 1, fontSize: '1rem', color: 'text.secondary' }} />
                }}
              />
            }
          />
          <DatePicker
            selected={endDate}
            onChange={(date) => setEndDate(date)}
            dateFormat="dd/MM/yyyy"
            customInput={
              <TextField 
                size="small" 
                fullWidth 
                InputProps={{
                  startAdornment: <CalendarToday sx={{ mr: 1, fontSize: '1rem', color: 'text.secondary' }} />
                }}
              />
            }
          />
        </Box>

        <Button variant="contained" fullWidth onClick={handleSearch} size="small">
          Buscar
        </Button>
      </Paper>

      {/* Lista de ventas en cards */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {filteredVentas.map((venta) => (
          <Card 
            key={venta.id}
            sx={{ 
              transition: 'all 0.2s',
              '&:hover': { boxShadow: 4 }
            }}
          >
            <CardContent sx={{ p: 2 }}>
              {/* Header del card */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Venta #{venta.id}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                    {new Date(venta.fecha).toLocaleDateString('es-ES', {
                      day: '2-digit', month: 'short', year: 'numeric',
                      hour: '2-digit', minute: '2-digit'
                    })}
                  </Typography>
                </Box>
                <Chip 
                  label={venta.estado} 
                  color={venta.estado === 'completada' ? 'success' : 'warning'}
                  size="small"
                  sx={{ fontSize: '0.7rem', height: 24 }}
                />
              </Box>

              {/* Información de la venta */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5 }}>
                {venta.cliente && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Person sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                      {venta.cliente}
                    </Typography>
                  </Box>
                )}
                {venta.sucursal && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Store sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                      {venta.sucursal}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Total y método de pago */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Total
                  </Typography>
                  <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold', fontSize: '1.3rem' }}>
                    ${formatCurrency(venta.total)}
                  </Typography>
                </Box>
                <Chip 
                  label={venta.metodo_pago}
                  variant="outlined"
                  size="small"
                  sx={{ fontSize: '0.75rem' }}
                />
              </Box>

              {/* Botón de ver detalle */}
              <Button
                fullWidth
                variant="outlined"
                size="small"
                startIcon={<Visibility />}
                onClick={() => handleVer(venta)}
              >
                Ver Detalle
              </Button>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Mensaje si no hay ventas */}
      {filteredVentas.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Receipt sx={{ fontSize: 80, color: 'grey.300', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No se encontraron ventas
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Intenta con otras fechas o búsqueda
          </Typography>
        </Box>
      )}

      {/* Botón flotante para nueva venta */}
      <Fab
        color="primary"
        onClick={() => navigate('/ventanueva')}
        sx={{
          position: 'fixed',
          bottom: 80,
          right: 16,
          zIndex: 1000
        }}
      >
        <AddCircle />
      </Fab>
    </Box>
  );

  // ==========================================
  // VISTA DESKTOP CON TABLA
  // ==========================================
  const DesktopView = () => (
    <Box sx={{ my: 3 }}>
      <Typography variant="h4" gutterBottom>Ventas</Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button 
          variant="contained" color="primary" 
          startIcon={<AddCircle />} onClick={() => navigate('/ventanueva')}
        >
          Nueva Venta
        </Button>

        <DatePicker
          selected={startDate}
          onChange={(date) => setStartDate(date)}
          selectsStart
          startDate={startDate}
          endDate={endDate}
          dateFormat="dd/MM/yyyy"
          customInput={<TextField label="Fecha Desde" size="small" />}
        />

        <DatePicker
          selected={endDate}
          onChange={(date) => setEndDate(date)}
          selectsEnd
          startDate={startDate}
          endDate={endDate}
          minDate={startDate}
          dateFormat="dd/MM/yyyy"
          customInput={<TextField label="Fecha Hasta" size="small" />}
        />

        <Button variant="contained" onClick={handleSearch}>Buscar</Button>

        <TextField
          label="Buscar por cliente o ID"
          variant="outlined" size="small"
          value={searchTerm} onChange={handleSearchTermChange}
          sx={{ minWidth: 250 }}
        />

        <IconButton onClick={exportToExcel} color="primary" title="Exportar a Excel">
          <SaveAlt />
        </IconButton>
      </Box>

      <div style={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={filteredVentas}
          columns={columns}
          getRowId={(row) => row.id}
          pageSize={15}
          rowsPerPageOptions={[15, 30, 50]}
          disableSelectionOnClick
          localeText={{
            noRowsLabel: 'No hay ventas para mostrar',
            MuiTablePagination: { labelRowsPerPage: 'Filas por página:' }
          }}
        />
      </div>
    </Box>
  );

  return (
    <Container maxWidth={isMobile ? false : 'xl'} sx={{ px: isMobile ? 1 : 3 }}>
      <MenuInicial />
      
      {loading ? (
        <Cargando />
      ) : (
        isMobile ? <MobileView /> : <DesktopView />
      )}

      {/* Diálogo de detalle */}
      <Dialog open={isDialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Detalle Venta #{selectedVenta?.id}
          <IconButton onClick={handleCloseDialog} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedVenta && (
            <>
              <Box sx={{ mb: 2, mt: 2 }}>
                <Typography><strong>Fecha:</strong> {new Date(selectedVenta.fecha).toLocaleString('es-ES')}</Typography>
                <Typography><strong>Cliente:</strong> {selectedVenta.cliente || 'N/A'}</Typography>
                <Typography><strong>Método de Pago:</strong> {selectedVenta.metodo_pago}</Typography>
                <Typography>
                  <strong>Estado:</strong> 
                  <Chip 
                    label={selectedVenta.estado} 
                    color={selectedVenta.estado === 'completada' ? 'success' : 'warning'}
                    size="small" sx={{ ml: 1 }}
                  />
                </Typography>
              </Box>

              <Typography variant="h6" gutterBottom>Productos</Typography>
              {ventaDetalles.length > 0 ? (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Producto</TableCell>
                        <TableCell align="right">Cant.</TableCell>
                        <TableCell align="right">Precio</TableCell>
                        <TableCell align="right">Subtotal</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ventaDetalles.map((detalle, index) => (
                        <TableRow key={index}>
                          <TableCell>{detalle.producto_nombre}</TableCell>
                          <TableCell align="right">{detalle.cantidad}</TableCell>
                          <TableCell align="right">${formatCurrency(detalle.precio_unitario)}</TableCell>
                          <TableCell align="right">${formatCurrency(detalle.subtotal)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={3} align="right"><strong>Total:</strong></TableCell>
                        <TableCell align="right"><strong>${formatCurrency(selectedVenta.total)}</strong></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="text.secondary">No hay detalles disponibles.</Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} variant="contained">Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Container>
  );
}

export default VentasList;
